import React, { useState } from "react";
import { Link } from "react-router-dom";

const Footer: React.FC = () => {
  const [email, setEmail] = useState("");
  const [submissionStatus, setSubmissionStatus] = useState({
    loading: false,
    message: "",
    success: false,
  });

  // Function to scroll to top of page
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setSubmissionStatus({
        loading: false,
        message: "Please enter a valid email",
        success: false,
      });
      return;
    }

    try {
      setSubmissionStatus({
        loading: true,
        message: "Subscribing...",
        success: false,
      });

      // Create FormData for web3forms
      const formData = new FormData();
      formData.append("email", email);
      formData.append("subject", "Newsletter Subscription");
      formData.append("from_name", "Newsletter Subscriber");
      formData.append("access_key", "3538f588-ad0a-41ee-8716-864a36b287df");

      // Add honeypot field for spam protection
      formData.append("botcheck", "");

      // Submit to web3forms
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setSubmissionStatus({
          loading: false,
          message: "Subscribed!",
          success: true,
        });
        setEmail(""); // Clear the input field

        // Reset success message after 3 seconds
        setTimeout(() => {
          setSubmissionStatus({
            loading: false,
            message: "",
            success: false,
          });
        }, 3000);
      } else {
        setSubmissionStatus({
          loading: false,
          message: "Failed to subscribe",
          success: false,
        });
      }
    } catch (error) {
      console.error("Newsletter submission error:", error);
      setSubmissionStatus({
        loading: false,
        message: "Error occurred",
        success: false,
      });
    }
  };

  return (
    <footer className="footer" id="footer">
      <div className="footer__curved curved-ellipse"></div>
      <div className="footer__curved-line curved-ellipse"></div>
      <div className="container footer-container">
        <div className="footer-left">
          <div className="footer-left__content">
            <Link className="footer-left__logo" to="/" onClick={scrollToTop}>
              <img src="/images/footer/spidersilk-logo-white.svg" alt="spiderSilk Logo" />
            </Link>
            <p className="footer-left__text">
              The platform that protects your business from open web and dark web vulnerabilities, third-party risks,
              and emerging threats.
            </p>
            <div className="footer-left__social-links">
              <a
                href="https://www.linkedin.com/company/spidersilk"
                className="footer-left__social-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/images/footer/linkedin-icon.svg" alt="LinkedIn" />
              </a>
            </div>
          </div>

          <div className="footer__newsletter">
            <h4 className="footer__newsletter-title">Newsletter Signup</h4>
            <form className="footer__newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="business email*"
                className={`footer__newsletter-input ${
                  submissionStatus.message && !submissionStatus.loading && !submissionStatus.success ? "error" : ""
                } ${submissionStatus.success ? "success" : ""}`}
                value={email}
                onChange={handleEmailChange}
                disabled={submissionStatus.loading}
              />
              <button
                type="submit"
                className={`footer__newsletter-button btn btn-primary ${submissionStatus.loading ? "loading" : ""}`}
                disabled={submissionStatus.loading}
              >
                {submissionStatus.loading ? (
                  <span className="loading-dot"></span>
                ) : (
                  <img src="/images/footer/arrow-icon.svg" alt="Submit" />
                )}
              </button>

              {/* Honeypot field to prevent spam */}
              <div style={{ display: "none" }}>
                <input type="text" name="botcheck" />
              </div>
            </form>
            {submissionStatus.message && (
              <div className={`footer__newsletter-status ${submissionStatus.success ? "success" : "error"}`}>
                {submissionStatus.message}
              </div>
            )}
          </div>
        </div>

        <div className="footer-right">
          <div className="footer-right__column">
            <h4 className="footer-right__column-title">Company</h4>
            <ul className="footer-right__column-list">
              <li className="footer-right__column-list-item">
                <Link to="/about" onClick={scrollToTop}>
                  About us
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <a href="#">Partners</a>
              </li>
            </ul>
          </div>

          <div className="footer-right__column">
            <h4 className="footer-right__column-title">Solutions</h4>
            <ul className="footer-right__column-list">
              <li className="footer-right__column-list-item">
                <Link to="/solutions/resonance" onClick={scrollToTop}>
                  Resonance
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/solutions/autonomous-soc" onClick={scrollToTop}>
                  SOC Agent
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/solutions/intelligence-platform" onClick={scrollToTop}>
                  Internet-wide Intelligence
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-right__column">
            <h4 className="footer-right__column-title">Industries</h4>
            <ul className="footer-right__column-list">
              <li className="footer-right__column-list-item">
                <Link to="/industries/government" onClick={scrollToTop}>
                  Government
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/industries/telecommunications" onClick={scrollToTop}>
                  Telecommunications
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/industries/energy" onClick={scrollToTop}>
                  Energy & Utilities
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/industries/financial" onClick={scrollToTop}>
                  Financial Services
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/industries/healthcare" onClick={scrollToTop}>
                  Healthcare
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/industries/retail" onClick={scrollToTop}>
                  Retail
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/industries/it" onClick={scrollToTop}>
                  IT & Services
                </Link>
              </li>
              <li className="footer-right__column-list-item">
                <Link to="/industries/manufacturing" onClick={scrollToTop}>
                  Manufacturing
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-right__column">
            <h4 className="footer-right__column-title">Contact</h4>
            <div className="footer-right__column-list">
              <ul className="footer-right__column-list">
                <li className="footer-right__column-list-item footer-right__contact-email">
                  <a href="mailto:info@spidersilk.com">
                    <img src="/images/footer/email-icon.svg" alt="Email" />
                    <span>info@spidersilk.com</span>
                  </a>
                </li>
                {/* <li className="footer-right__column-list-item">
                  <a href="#">Contact Sales</a>
                </li>
                <li className="footer-right__column-list-item">
                  <a href="#">Contact Support</a>
                </li> */}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
